// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dialog component for resolving asset exceptions.
 * Provides a comprehensive interface for editing asset data to resolve
 * both date and detail exceptions, with product default suggestions
 * and clear guidance for each exception type.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { SparseAsset } from '@/asset/api/types';
import { assetResponseSchema } from '@/asset/hooks/use-asset';
import { SupportSnackbar } from '@/components/support-snackbar';
import { ExceptionType } from '@/constants/asset-exception-type';
import { CheckCircle, OpenInNew } from '@/constants/hardfin-standard-icons';
import { Currency } from '@/currency/api/types';
import { toSerializableCurrency } from '@/currency/utilities/to-form-value';
import { getItem } from '@/items/api/get-item';
import { ItemAccounting } from '@/items/api/types';
import { getDateInconsistencies } from '@/report/api/get-date-inconsistencies';
import { getDetailInconsistencies } from '@/report/api/get-detail-inconsistencies';
import {
  DateExceptionFormData,
  DetailExceptionFormData,
  dateExceptionFormSchema,
  detailExceptionFormSchema,
} from '@/report/api/types';
import { DateExceptionResolutionForm } from '@/report/components/date-exception-resolution-form';
import { DetailExceptionResolutionForm } from '@/report/components/detail-exception-resolution-form';
import { ONE_MINUTE_MS } from '@/utilities/time-constants';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

/**
 * Props for the AssetExceptionResolutionDialog component.
 */
interface Props {
  /**
   * Controls the visibility of the dialog.
   */
  open: boolean;

  /**
   * Callback fired when the dialog requests to be closed.
   */
  onClose: () => void;

  /**
   * Callback fired when the dialog has fully exited (after close animation completes).
   * Use this to clear selected asset data to prevent flicker during close animation.
   */
  onExited?: () => void;

  /**
   * Callback fired when all exceptions for the asset have been resolved.
   * Use this to optimistically update the table UI before the full data refetch.
   */
  onResolved?: (assetId: string) => void;

  /**
   * The asset with exceptions to resolve.
   * Contains basic asset information and key for navigation.
   * Optional to allow dialog to remain mounted when no asset is selected.
   */
  asset?: SparseAsset;

  /**
   * The type of exceptions being resolved in this dialog.
   * Determines which form to display.
   * Optional to allow dialog to remain mounted when no asset is selected.
   */
  exceptionType?: 'date' | 'detail';

  /**
   * Array of exception types affecting this asset.
   * Used to display relevant fields in the resolution form.
   * Optional to allow dialog to remain mounted when no asset is selected.
   */
  exceptions?: ExceptionType[];

  /**
   * Date data for resolving date exceptions.
   * Only required when exceptionType is 'date'.
   */
  dateData?: {
    inventoryDate: DateTime;
    inServiceDate: DateTime | null;
    scrapDate: DateTime | null;
    firstSaleDate: DateTime | null;
    firstAssignmentDate: DateTime | null;
    firstMovementDate: DateTime | null;
  };

  /**
   * Detail data for resolving detail exceptions.
   * Only required when exceptionType is 'detail'.
   */
  detailData?: {
    inServiceDate: DateTime | null;
    usefulLife: number | null;
    inventoryCostBasis: string | null;
    deployedCostBasis: string | null;
    salvageValue: string | null;
  };
}

/**
 * Converts an optional currency to the form's amount string.
 */
function toFormAmount(value: Currency | null): string | null {
  return value === null ? null : toSerializableCurrency(value).amount;
}

/**
 * Dialog component for resolving asset exceptions.
 * Provides a form interface for editing asset data to fix both date
 * and detail inconsistencies, with product default suggestions and
 * clear guidance for each exception type.
 *
 * Features:
 * - Dual form support (date vs detail exceptions)
 * - Asset information header
 * - Link to full asset details page
 * - Product default suggestions
 * - Form validation
 * - Loading and error states
 * - Success feedback
 *
 * Usage example:
 * ```tsx
 * function ExceptionReport() {
 *   const [open, setOpen] = useState(false);
 *
 *   return (
 *     <AssetExceptionResolutionDialog
 *       open={open}
 *       onClose={() => setOpen(false)}
 *       asset={exceptionItem.asset}
 *       exceptionType="date"
 *       exceptions={exceptionItem.exceptionTypes}
 *       dateData={exceptionItem}
 *     />
 *   );
 * }
 * ```
 */

export function AssetExceptionResolutionDialog(props: Props) {
  const [isError, setIsError] = useState(false);

  // Fetch current asset data to enable live updates when user changes data in another window
  // Only fetch when we have a valid asset key and dealing with detail exceptions
  const assetQuery = useQuery({
    queryKey: ['asset', props.asset?.key],
    queryFn: async () => {
      const url = `/v1/asset/${props.asset!.key}`;
      const response = await api.get(url);
      const result = await parseResponse(assetResponseSchema, response.data, {
        schemaName: 'Asset',
        endpoint: url,
      });
      return result.data;
    },
    enabled: props.exceptionType === 'detail' && !!props.asset?.key,
    refetchInterval: ONE_MINUTE_MS,
    refetchOnWindowFocus: true,
  });

  const fetchedAsset = assetQuery.data ?? null;

  // Fetch date inconsistencies for this specific asset to enable live updates
  // Uses assetIds to filter to just this asset's ID
  const dateInconsistenciesQuery = useQuery({
    queryKey: [
      'report',
      'exception',
      'asset',
      'date-inconsistencies',
      { assetIds: [props.asset?.id] },
    ],
    queryFn: () =>
      getDateInconsistencies({
        assetIds: props.asset?.id ? [props.asset.id] : undefined,
      }),
    enabled: props.exceptionType === 'date' && !!props.asset?.id,
    refetchInterval: ONE_MINUTE_MS,
    refetchOnWindowFocus: true,
  });

  // Find the current asset in the date inconsistencies data
  const fetchedDateData =
    props.exceptionType === 'date' && dateInconsistenciesQuery.data
      ? dateInconsistenciesQuery.data.assets.find(
          (item) => item.asset.id === props.asset?.id
        )
      : null;

  // Detect if all date exceptions have been resolved (query succeeded but asset not found)
  const isDateExceptionResolved =
    props.exceptionType === 'date' &&
    dateInconsistenciesQuery.isSuccess &&
    !fetchedDateData;

  // Fetch detail inconsistencies for this specific asset to enable live updates
  // Uses assetIds to filter to just this asset's ID
  const detailInconsistenciesQuery = useQuery({
    queryKey: [
      'report',
      'exception',
      'asset',
      'detail-inconsistencies',
      { assetIds: [props.asset?.id] },
    ],
    queryFn: () =>
      getDetailInconsistencies({
        assetIds: props.asset?.id ? [props.asset.id] : undefined,
      }),
    enabled: props.exceptionType === 'detail' && !!props.asset?.id,
    refetchInterval: ONE_MINUTE_MS,
    refetchOnWindowFocus: true,
  });

  // Find the current asset in the detail inconsistencies data
  const fetchedDetailData =
    props.exceptionType === 'detail' && detailInconsistenciesQuery.data
      ? detailInconsistenciesQuery.data.assets.find(
          (item) => item.asset.id === props.asset?.id
        )
      : null;

  // Detect if all detail exceptions have been resolved (query succeeded but asset not found)
  const isDetailExceptionResolved =
    props.exceptionType === 'detail' &&
    detailInconsistenciesQuery.isSuccess &&
    !fetchedDetailData;

  // Notify parent when exceptions are resolved for optimistic UI updates
  const { onResolved, asset } = props;
  useEffect(() => {
    if (onResolved && asset?.id) {
      if (isDateExceptionResolved || isDetailExceptionResolved) {
        onResolved(asset.id);
      }
    }
  }, [isDateExceptionResolved, isDetailExceptionResolved, asset, onResolved]);

  // Fetch item details to get accounting defaults for detail exceptions
  // Only fetch when dealing with detail exceptions and asset is available
  const itemQuery = useQuery({
    queryKey: ['item', props.asset?.item.key],
    queryFn: () => getItem(props.asset!.item.key),
    enabled: props.exceptionType === 'detail' && !!props.asset,
  });

  const itemDefaults: ItemAccounting | null =
    props.exceptionType === 'detail' && itemQuery.data
      ? itemQuery.data.item.accounting
      : null;

  // Store original data for change detection (use fetched data for live updates)
  // When fetchedDateData exists, use its values (including nulls) to ensure we get
  // the latest state. Only fall back to props when fetchedDateData doesn't exist yet.
  const originalDateData: DateExceptionFormData =
    props.exceptionType === 'date' && props.dateData
      ? fetchedDateData
        ? {
            inventoryDate: fetchedDateData.inventoryDate,
            inServiceDate: fetchedDateData.inServiceDate,
            scrapDate: fetchedDateData.scrapDate,
            firstSaleDate: fetchedDateData.firstSaleDate,
            firstAssignmentDate: fetchedDateData.firstAssignmentDate,
            firstMovementDate: fetchedDateData.firstMovementDate,
          }
        : {
            inventoryDate: props.dateData.inventoryDate,
            inServiceDate: props.dateData.inServiceDate,
            scrapDate: props.dateData.scrapDate,
            firstSaleDate: props.dateData.firstSaleDate,
            firstAssignmentDate: props.dateData.firstAssignmentDate,
            firstMovementDate: props.dateData.firstMovementDate,
          }
      : {
          inventoryDate: null,
          inServiceDate: null,
          scrapDate: null,
          firstSaleDate: null,
          firstAssignmentDate: null,
          firstMovementDate: null,
        };

  // Date exception form
  const dateForm = useForm<DateExceptionFormData>({
    resolver: zodResolver(dateExceptionFormSchema),
    values: originalDateData,
  });

  // Store original data for change detection (use fetched asset for live updates on all available fields)
  // When fetchedAsset exists, use its values (including nulls) to ensure we get
  // the latest state. Only fall back to props when fetchedAsset doesn't exist yet.
  const originalDetailData: DetailExceptionFormData =
    props.exceptionType === 'detail' && props.detailData
      ? fetchedAsset
        ? {
            inServiceDate:
              fetchedAsset.accounting.inServiceDate?.toISO() ?? null,
            usefulLife:
              fetchedAsset.accounting.usefulLife !== null &&
              fetchedAsset.accounting.usefulLife !== undefined
                ? String(fetchedAsset.accounting.usefulLife)
                : null,
            salvageValue: toFormAmount(fetchedAsset.accounting.salvageValue),
            inventoryCostBasis: toFormAmount(
              fetchedAsset.accounting.acquisitionCogs
            ),
            deployedCostBasis: toFormAmount(
              fetchedAsset.accounting.totalPotentialCOGS
            ),
          }
        : {
            inServiceDate: props.detailData.inServiceDate?.toISO() ?? null,
            usefulLife:
              props.detailData.usefulLife !== null
                ? String(props.detailData.usefulLife)
                : null,
            salvageValue: props.detailData.salvageValue ?? null,
            inventoryCostBasis: props.detailData.inventoryCostBasis,
            deployedCostBasis: props.detailData.deployedCostBasis,
          }
      : {
          inServiceDate: null,
          usefulLife: null,
          inventoryCostBasis: null,
          deployedCostBasis: null,
          salvageValue: null,
        };

  // Detail exception form
  const detailForm = useForm<DetailExceptionFormData>({
    resolver: zodResolver(detailExceptionFormSchema),
    values: originalDetailData,
  });

  /**
   * Closes the dialog and resets form state.
   */
  const close = () => {
    props.onClose();
    dateForm.reset();
    detailForm.reset();
    setIsError(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.open}
      onClose={close}
      TransitionProps={{
        onExited: props.onExited,
      }}
    >
      <SupportSnackbar
        isOpen={isError}
        onClose={() => setIsError(false)}
        message="An error occurred while resolving the exception."
      />

      <DialogTitle>Resolve asset exceptions</DialogTitle>

      <DialogContent dividers>
        {props.asset && props.exceptions && props.exceptionType ? (
          <Stack spacing={2}>
            {/* Asset information header */}
            <Box>
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Stack direction="column" spacing={0.5} alignItems="left">
                  <Typography variant="body1" fontWeight="medium">
                    {props.asset.serial}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {props.asset.item.name}
                  </Typography>
                </Stack>
                <Link
                  href={`/assets/${props.asset.key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    marginTop: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  View full asset details
                  <OpenInNew fontSize="inherit" />
                </Link>
              </Stack>
            </Box>

            <Divider />

            {/* Exception type header */}
            <Typography variant="h6">
              {props.exceptionType === 'date' ? 'Date' : 'Detail'} exceptions
            </Typography>

            {/* Appropriate form based on exception type */}
            {props.exceptionType === 'date' && props.dateData ? (
              isDateExceptionResolved ? (
                <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
                  <CheckCircle color="success" sx={{ fontSize: 48 }} />
                  <Typography variant="h6" color="success.main">
                    All exceptions resolved
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    This asset no longer has any date exceptions
                  </Typography>
                </Stack>
              ) : (
                <DateExceptionResolutionForm
                  exceptionTypes={props.exceptions}
                  control={dateForm.control}
                  assetKey={props.asset.key}
                  assetSerial={props.asset.serial}
                  dateData={
                    fetchedDateData
                      ? {
                          inventoryDate: fetchedDateData.inventoryDate,
                          inServiceDate: fetchedDateData.inServiceDate,
                          scrapDate: fetchedDateData.scrapDate,
                          firstSaleDate: fetchedDateData.firstSaleDate,
                          firstAssignmentDate:
                            fetchedDateData.firstAssignmentDate,
                          firstMovementDate: fetchedDateData.firstMovementDate,
                        }
                      : {
                          inventoryDate: props.dateData.inventoryDate,
                          inServiceDate: props.dateData.inServiceDate,
                          scrapDate: props.dateData.scrapDate,
                          firstSaleDate: props.dateData.firstSaleDate,
                          firstAssignmentDate:
                            props.dateData.firstAssignmentDate,
                          firstMovementDate: props.dateData.firstMovementDate,
                        }
                  }
                />
              )
            ) : props.exceptionType === 'detail' && props.detailData ? (
              isDetailExceptionResolved ? (
                <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
                  <CheckCircle color="success" sx={{ fontSize: 48 }} />
                  <Typography variant="h6" color="success.main">
                    All exceptions resolved
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    This asset no longer has any detail exceptions
                  </Typography>
                </Stack>
              ) : !fetchedAsset ? (
                // The asset names the currency every cost field on this form is entered in, so
                // wait for it rather than labelling and saving the inputs as the org default.
                <Stack alignItems="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </Stack>
              ) : (
                <DetailExceptionResolutionForm
                  exceptionTypes={props.exceptions}
                  itemDefaults={itemDefaults}
                  control={detailForm.control}
                  setValue={detailForm.setValue}
                  assetKey={props.asset.key}
                  costCurrencyCode={fetchedAsset.accounting.costCurrencyCode}
                  assetSerial={props.asset.serial}
                  detailData={
                    fetchedAsset
                      ? {
                          inServiceDate:
                            fetchedAsset.accounting.inServiceDate?.toISO() ??
                            null,
                          usefulLife:
                            fetchedAsset.accounting.usefulLife ?? null,
                          inventoryCostBasis: toFormAmount(
                            fetchedAsset.accounting.acquisitionCogs
                          ),
                          deployedCostBasis: toFormAmount(
                            fetchedAsset.accounting.totalPotentialCOGS
                          ),
                          salvageValue: toFormAmount(
                            fetchedAsset.accounting.salvageValue
                          ),
                        }
                      : {
                          inServiceDate:
                            props.detailData.inServiceDate?.toISO() ?? null,
                          usefulLife: props.detailData.usefulLife,
                          inventoryCostBasis:
                            props.detailData.inventoryCostBasis,
                          deployedCostBasis: props.detailData.deployedCostBasis,
                          salvageValue: props.detailData.salvageValue,
                        }
                  }
                />
              )
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={close} variant="text">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
